import FightTileLabelPopup from "@/components/FightSystem/FightTileLabelPopup.vue";
import {
	TILE_LABEL_CORRECTION,
	UNSUPPORTED_BITMAP_LANGUAGES,
} from "@/config/constants";
import { eventRepository } from "@/event";
import useEmitter, { EmitterEvents, TileLabelAction } from "@/hooks/useEmitter";
import useSiegeSystemRepository from "@/hooks/useSiegeSystemRepository";
import useTileRepository from "@/hooks/useTileRepository";
import { PopupType, usePopupStore } from "@/store/usePopupStore";
import useSettingsStore from "@/store/useSettingsStore";
import useSettlementsStore from "@/store/useSettlementsStore";
import useUserStore from "@/store/useUserStore";
import type {
	SettlementItem,
	TileCoordinates,
	TileLabelTypeConfig,
} from "@/types/tile";
import {
	TileLabelSkin,
	TileLabelType,
	tileLabelTypes,
	tileLabelZoomBehaviorDefaults,
	tileLabelZoomConfig,
} from "@/types/tile";
import { SkinTypes } from "@/types/ui";
import { translate } from "@/utils/translate";
import { markRaw } from "vue";
import type SettlementCastleBuildingTileEntity from "./entities/buildings/SettlementCastleBuildingTileEntity";
import { settlementNames } from "./entities/config/settlements";
import { UpgradeIds } from "./entities/config/upgrades";
import { plagueRepository } from "./PlagueRepository";
import QuestRepository from "./QuestRepository";
import { getUpgradeCurrentReplaceValueById } from "./utils/upgrades";

class TileLabel {
	private labelContainer: Phaser.GameObjects.Container | null = null;
	private text:
		| Phaser.GameObjects.Text
		| Phaser.GameObjects.BitmapText
		| null = null;
	private ribbon: Phaser.GameObjects.NineSlice | null = null;
	private bg: Phaser.GameObjects.Sprite | null = null;
	private state: number = -1;
	private readonly tilerepo: ReturnType<typeof useTileRepository>;
	public active = true;
	private lastAppliedFontSize: number = -1;
	private lastAppliedFontColor: number = -1;
	public sharingTile: boolean = false; // used for quest labels to indicate that this tile is shared between multiple labels, so that level labels can be hidden
	private scene: Phaser.Scene;
	private resizeGroup1: Array<
		Phaser.GameObjects.TileSprite | Phaser.GameObjects.Rectangle
	> = [];
	private resizeGroup2: Array<
		Phaser.GameObjects.TileSprite | Phaser.GameObjects.Rectangle
	> = [];
	private hideLabel: boolean = false;
	public secondText:
		| Phaser.GameObjects.Text
		| Phaser.GameObjects.BitmapText
		| null = null;

	private chestTimer: Phaser.Time.TimerEvent | null = null;

	constructor(
		scene: Phaser.Scene,
		private coordinates: TileCoordinates & { offsetY?: number },
		private scaleToZoom: boolean = false,
		innerText?: string,
		private type: TileLabelType = TileLabelType.SETTLEMENT,
		public skin: TileLabelSkin = TileLabelSkin.LEVEL,
		innerTextSecond?: string,
	) {
		this.tilerepo = useTileRepository();
		if (skin) {
			this.skin = skin;
		}

		const config = this.getConfig();

		const { TILE_SIZE } = this.tilerepo;

		const x = this.coordinates.x * TILE_SIZE;
		const y = this.coordinates.y * TILE_SIZE;

		this.scene = scene;

		this.labelContainer = this.scene.add
			.container(
				x,
				// offset sa musi pridat len tu, aby sa vygeneroval kluc podla x/y, a nie podla x/y+offset
				y + (this.coordinates.offsetY ?? 0) * TILE_SIZE,
			)
			.setSize(TILE_SIZE, TILE_SIZE)
			.setDepth(TILE_LABEL_CORRECTION);

		const assetName = "tile-label-sprite-" + config.asset;
		this.bg = this.scene.add.sprite(
			0,
			0,
			assetName,
			config.backgroundFrameMain,
		);

		// NOTE: to consider other parameters, we need to have the text already added
		const userStore = useUserStore();
		const useBitmapText =
			this.skin !== TileLabelSkin.TUTORIAL ||
			!UNSUPPORTED_BITMAP_LANGUAGES.includes(userStore.language);
		if (
			[
				TileLabelSkin.FIGHT_SYSTEM_1,
				TileLabelSkin.FIGHT_SYSTEM_2,
				TileLabelSkin.FIGHT_SYSTEM_3,
				TileLabelSkin.FIGHT_SYSTEM_4,
			].includes(this.skin)
		) {
			innerText = translate(config.defaultText ?? "").toUpperCase();
			if (innerText.length > 21)
				innerText = innerText.substring(0, 19) + "...";
		}
		if (innerText) {
			this.text = useBitmapText
				? this.scene.add
						.bitmapText(
							0,
							0,
							"texturina",
							innerText,
							config.fontSize ?? 0,
						)
						.setTint(config.fontColor ?? 0x000000)
						.setOrigin(0.5, 1)
				: this.scene.add.text(0, 0, innerText, config).setOrigin(0.5);
			if (this.text === null) return;
			if (innerTextSecond) {
				this.secondText = useBitmapText
					? this.scene.add
							.bitmapText(
								0,
								0,
								"texturina",
								innerTextSecond,
								config.fontSize ?? 0,
							)
							.setTint(config.fontColor ?? 0x000000)
							.setOrigin(0.5, 1)
					: this.scene.add
							.text(0, 0, innerTextSecond, config)
							.setOrigin(0.5);
				if (this.secondText === null) return;
			}
		}

		if (this.skin === TileLabelSkin.TUTORIAL) {
			this.labelContainer.setVisible(false);

			this.text!.y += this.secondText ? -40 : 4;
			if (this.secondText) {
				this.secondText!.y += 40;
			}
			const widthPadding = 75;
			const tutsLabelBgWidth =
				Math.max(this.text!.width, this.secondText?.width ?? 0) / 2 +
					widthPadding || (innerText?.length ?? 1) * 30;
			const tutsLabelBgHeight = this.secondText ? 200 : 100;
			this.labelContainer.setSize(tutsLabelBgWidth, tutsLabelBgHeight);

			const tutsLabelBgWidthMargin = 4;
			const tutsLabelBgHeightMargin = 4;

			const bgRectBase = this.scene.add.rectangle(
				(tutsLabelBgWidth / 2) * -1 - tutsLabelBgWidthMargin,
				(tutsLabelBgHeight / 2) * -1 - tutsLabelBgHeightMargin,
				tutsLabelBgWidth + tutsLabelBgWidthMargin * 2,
				tutsLabelBgHeight + tutsLabelBgHeightMargin * 2,
				0x3b3b3b,
			);
			bgRectBase.setOrigin(0);
			this.labelContainer.add(bgRectBase);
			this.resizeGroup2.push(bgRectBase);

			const bgRect = this.scene.add.rectangle(
				(tutsLabelBgWidth / 2) * -1,
				(tutsLabelBgHeight / 2) * -1,
				tutsLabelBgWidth,
				tutsLabelBgHeight,
				0xf2dcb5,
			);
			bgRect.setOrigin(0);

			this.labelContainer.add(bgRect);
			this.resizeGroup1.push(bgRect);

			const bgTexture = this.scene.add.tileSprite(
				(tutsLabelBgWidth / 2) * -1,
				(tutsLabelBgHeight / 2) * -1,
				tutsLabelBgWidth,
				tutsLabelBgHeight,
				"tutorial-label-bg-texture",
			);
			bgTexture.setOrigin(0);
			// Set texture size relative to its potential repetition within the defined tileSprite Game Object size
			bgTexture.tileScaleX = tileLabelZoomConfig[1];
			bgTexture.tileScaleY = tileLabelZoomConfig[1];
			bgTexture.setBlendMode(Phaser.BlendModes.MULTIPLY);

			const arrowWidth = 30;
			const img = this.scene.add.image(
				(arrowWidth / 2) * -1,
				tutsLabelBgHeight / 2,
				"tutorial-label-arrow-down",
			);
			img.setSize(arrowWidth, arrowWidth);
			img.setOrigin(0);

			this.labelContainer.add(img);

			this.labelContainer.add(bgTexture);
			this.resizeGroup1.push(bgTexture);
			this.labelContainer.setX(this.coordinates.x * TILE_SIZE);
			const closeBtn = this.scene.add.image(0, 0, "close-btn");
			closeBtn.setPosition(
				tutsLabelBgWidth / 2 - closeBtn.width / 4,
				0 - closeBtn.height / (this.secondText ? 1 : 2),
			);
			closeBtn.setOrigin(0, 0.5);
			closeBtn.setScale(0.75);
			closeBtn.setInteractive({ useHandCursor: true });
			closeBtn.on("pointerup", (pointer: Phaser.Input.Pointer): void => {
				if (pointer.downElement?.nodeName !== "CANVAS") return;

				this.hideForWhile();
			});
			this.labelContainer.add(closeBtn);
		} else if (this.skin === TileLabelSkin.MAIN_SETTLEMENT_NAME) {
			this.makeMainSettlementNameLabel();
			this.labelContainer.setX(this.coordinates.x * TILE_SIZE);

			this.labelContainer.setY(
				this.coordinates.y * TILE_SIZE - TILE_SIZE / 2 - 40,
			);
			this.labelContainer.setVisible(true);
		} else if (
			[
				TileLabelSkin.QUEST_MAIN_Q,
				TileLabelSkin.QUEST_MAIN_E,
				TileLabelSkin.QUEST_SIDE_Q,
				TileLabelSkin.QUEST_SIDE_E,
			].includes(this.skin)
		) {
			this.bg.setFrame(config.backgroundFrameMain);
			this.labelContainer.add(this.bg);
		} else if (
			[TileLabelSkin.PLAGUE_GREEN, TileLabelSkin.PLAGUE_WHITE].includes(
				this.skin,
			)
		) {
			this.labelContainer.setDepth(TILE_LABEL_CORRECTION + 9);
			this.labelContainer.add(this.bg);
		} else {
			this.labelContainer.add(this.bg);
			this.labelContainer.setDepth(TILE_LABEL_CORRECTION + 10);
		}
		if (config.yOffset !== undefined) {
			this.bg.setOrigin(0.5, config.yOffset);
		}
		if (this.text) {
			// NOTE: to stack correctly without needing to override the layer's depth via setDepth
			this.labelContainer.add(this.text);
		}
		if (this.secondText) {
			// NOTE: to stack correctly without needing to override the layer's depth via setDepth
			this.labelContainer.add(this.secondText);
		}

		if (this.scaleToZoom) {
			if (this.skin !== TileLabelSkin.MAIN_SETTLEMENT_NAME) {
				// prevents clicking on the tile below the settlement name
				this.labelContainer.setInteractive();
			}
			this.labelContainer.on(
				"pointerdown",
				(pointer: Phaser.Input.Pointer): void => {
					if (pointer.downElement?.nodeName !== "CANVAS") return;

					const tile = this.tilerepo.getTileEntityByTileCoordinates(
						this.coordinates,
					);
					if (tile) {
						useEmitter().emit(EmitterEvents.PhaserTileLabelClick, {
							tile,
						});
					}
				},
			);
		}

		if (this.skin === TileLabelSkin.MAIN_SETTLEMENT_NAME) {
			useEmitter().on(EmitterEvents.SettlementPropsUpdate, (): void => {
				this.makeMainSettlementNameLabel(true);
			});
		}

		if (this.skin === TileLabelSkin.CHEST) {
			this.labelContainer.setInteractive();
			this.labelContainer.on(
				"pointerup",
				(pointer: Phaser.Input.Pointer): void => {
					if (pointer.downElement?.nodeName !== "CANVAS") return;

					this.shakeAnimation();
				},
			);
			this.chestTimer = this.scene.time.addEvent({
				delay: 10_000,
				callback: this.shakeAnimation,
				callbackScope: this,
				loop: true,
			});
		}
		if ([TileLabelSkin.PLAGUE_GREEN].includes(this.skin)) {
			this.labelContainer.setInteractive();

			let longPressTimer: Phaser.Time.TimerEvent | null = null;
			let repeatHealTimer: Phaser.Time.TimerEvent | null = null;

			const stopAll = (): void => {
				if (longPressTimer) {
					longPressTimer.destroy();
					longPressTimer = null;
				}
				if (repeatHealTimer) {
					repeatHealTimer.destroy();
					repeatHealTimer = null;
				}
				// Tu vráť dragging mapy
				// this.scene.mapDrag.enabled = true;
			};

			const healAction = (): void => {
				if (this.skin === TileLabelSkin.PLAGUE_WHITE) {
					stopAll();
					return;
				}

				const callback = (): void => {
					const tileIndex =
						this.tilerepo.getTileIndexByTileCoordinates(
							this.coordinates,
						);
					plagueRepository.healPlague(tileIndex);
				};

				useEmitter().emit(EmitterEvents.PhaserTileLabelClick, {
					tile: null,
					callback,
				});
			};

			const startLongPressLogic = (): void => {
				if (this.skin === TileLabelSkin.PLAGUE_WHITE) return;

				stopAll(); // Poistka

				// Nastavíme časovač na 500ms pre "long press"
				longPressTimer = this.scene.time.delayedCall(500, () => {
					// Ak sme tu, znamená to, že užívateľ políčko drží dosť dlho
					// Zastavíme dragging mapy
					// this.scene.mapDrag.enabled = false;

					const speedMultiplier =
						1 +
						getUpgradeCurrentReplaceValueById(
							UpgradeIds.finger_on_the_pulse,
						) +
						getUpgradeCurrentReplaceValueById(
							UpgradeIds.lazy_finger,
						);

					const intervalTime = 500 / speedMultiplier;

					repeatHealTimer = this.scene.time.addEvent({
						delay: intervalTime,
						callback: healAction,
						loop: true,
					});
				});
			};

			this.labelContainer.on(
				"pointerdown",
				(pointer: Phaser.Input.Pointer) => {
					if (pointer.downElement?.nodeName !== "CANVAS") return;
					startLongPressLogic();
				},
			);

			this.labelContainer.on("pointerup", () => {
				if (longPressTimer && longPressTimer.getProgress() < 1) {
					healAction();
				}
				stopAll();
			});

			this.labelContainer.on("pointerout", stopAll);

			this.scene.input.on("pointerup", stopAll);

			this.labelContainer.once("destroy", () => {
				stopAll();
				this.scene.input.off("pointerup", stopAll);
			});
		}
		if (
			[
				TileLabelSkin.EASTER_EGG,
				TileLabelSkin.EASTER_EGG_GOLDEN,
				TileLabelSkin.HALLOWEEN_PUMPKIN,
				TileLabelSkin.HALLOWEEN_JACK_O_LANTERN,
				TileLabelSkin.PIRATES_MAP,
				TileLabelSkin.PIRATES_TREASURE,
			].includes(this.skin)
		) {
			this.labelContainer.setInteractive();
			this.labelContainer.on(
				"pointerup",
				(pointer: Phaser.Input.Pointer): void => {
					if (pointer.downElement?.nodeName !== "CANVAS") return;
					const callback = (): void => {
						eventRepository.claimCollectable(this.coordinates);
					};
					useEmitter().emit(EmitterEvents.PhaserTileLabelClick, {
						tile: null,
						callback,
					});
				},
			);
		}
		if ([TileLabelSkin.QUEST_SIDE_E].includes(this.skin)) {
			this.labelContainer.setInteractive();
			this.labelContainer.on(
				"pointerup",
				(pointer: Phaser.Input.Pointer): void => {
					if (pointer.downElement?.nodeName !== "CANVAS") return;
					const callback = (): void => {
						QuestRepository.getInstance().showPopupSideQuestByCoordinates(
							this.coordinates,
						);
					};
					useEmitter().emit(EmitterEvents.PhaserTileLabelClick, {
						tile: null,
						callback,
					});
				},
			);
		}
		if (
			[
				TileLabelSkin.FIGHT_SYSTEM_1,
				TileLabelSkin.FIGHT_SYSTEM_2,
				TileLabelSkin.FIGHT_SYSTEM_3,
				TileLabelSkin.FIGHT_SYSTEM_4,
			].includes(this.skin)
		) {
			this.labelContainer.removeInteractive();
			this.text?.setScale(0.5);
			this.text?.setOrigin(0.45, 1.15);
			this.text?.setTint(0xf4d278);
			const closeBtn = this.scene.add.image(0, 0, "close-btn");
			closeBtn.setOrigin(0.5, 1);
			closeBtn.setPosition(this.bg!.width / 2, 0);
			closeBtn.setScale(0.75);
			closeBtn.setInteractive({ useHandCursor: true });
			closeBtn.on("pointerup", (pointer: Phaser.Input.Pointer): void => {
				if (pointer.downElement?.nodeName !== "CANVAS") return;

				useEmitter().emit(EmitterEvents.PhaserTileLabelClick, {
					tile: null,
				});
				useEmitter().emit(EmitterEvents.PhaserUpdateTileLabel, {
					coordinates: this.coordinates,
					skin: this.skin,
					action: TileLabelAction.DELETE,
				});
			});
			this.labelContainer.add(closeBtn);
			const infoBtn = this.scene.add.image(0, 0, "i-btn");
			infoBtn.setOrigin(0.6, 0.7);
			infoBtn.setPosition(-this.bg!.width / 2 + infoBtn.width / 2, 0);
			infoBtn.setScale(0.6);
			infoBtn.setInteractive({ useHandCursor: true });
			infoBtn.on("pointerup", (pointer: Phaser.Input.Pointer): void => {
				if (pointer.downElement?.nodeName !== "CANVAS") return;

				useEmitter().emit(EmitterEvents.PhaserTileLabelClick, {
					tile: null,
				});
				usePopupStore().showPopup({
					type: PopupType.INFO,
					skin: SkinTypes.DARK,
					isPaddingless: true,
					isCentered: true,
					isPriority: true,
					height: "auto",
					width: 23.875,
					component: markRaw(FightTileLabelPopup),
					componentProps: {
						coordinates: this.coordinates,
					},
				});
			});
			this.labelContainer.add(infoBtn);
		}
		if (
			[
				TileLabelSkin.FIGHT_SYSTEM_DEFEATED_ENEMY,
				TileLabelSkin.FIGHT_SYSTEM_DEFEATED_FRIENDLY,
			].includes(this.skin)
		) {
			this.labelContainer.setInteractive();
			this.labelContainer.on(
				"pointerup",
				(pointer: Phaser.Input.Pointer): void => {
					if (pointer.downElement?.nodeName !== "CANVAS") return;

					useEmitter().emit(EmitterEvents.PhaserTileLabelClick, {
						tile: null,
					});

					useSiegeSystemRepository().removeDefeatedUnitLabel(
						this.coordinates,
					);
				},
			);
		}
	}

	public shakeAnimation(): void {
		if (!this.labelContainer || !useSettingsStore().animationsOn) return;

		const { TILE_SIZE } = this.tilerepo;
		const defaultY = this.coordinates.y * TILE_SIZE;
		this.scene.tweens.add({
			targets: this.labelContainer,
			y: defaultY - 20,
			duration: 200,
			yoyo: true,
			ease: "Power1",
		});

		this.scene.tweens.chain({
			targets: this.labelContainer,
			tweens: [
				{ angle: 0, duration: 0 }, // 0ms
				{ angle: -6, duration: 80 }, // 80ms
				{ angle: 0, duration: 120 }, // 200ms
				{ angle: 6, duration: 120 }, // 320ms
				{ angle: -3, duration: 80 }, // 400ms
				{ angle: 2, duration: 80 }, // 480ms
				{ angle: 0, duration: 40 }, // 520ms
				{ angle: 0, y: defaultY, duration: 40 },
			],
			onComplete: (): void => {
				if (!this.labelContainer) return;
				this.labelContainer.angle = 0;
				this.labelContainer.y = defaultY;
			},
		});
	}

	public get container(): Phaser.GameObjects.Container | null {
		return this.labelContainer ?? null;
	}

	public get rawText(): string {
		return this.text?.text ?? "";
	}

	public makeMainSettlementNameLabel(force: boolean = false): void {
		if (this.text === null) return;
		if (this.labelContainer === null) return;

		// NOTE: force = refresh after changing the name of the main settlement
		if (force) {
			this.labelContainer.remove([this.ribbon!, this.text]);

			const mainSettlement: SettlementItem | undefined =
				settlementNames.get(1);
			if (!mainSettlement) return;

			const entity = this.tilerepo.getTileEntityByTileCoordinates({
				x: mainSettlement.x,
				y: mainSettlement.y,
			}) as SettlementCastleBuildingTileEntity | null;
			const text = entity!.settlementName;

			const config = this.getConfig();

			this.text = this.scene.add
				.bitmapText(0, 0, "texturina", text, config.fontSize ?? 0)
				.setTint(config.fontColor ?? 0x000000)
				.setOrigin(0.5, 1);
		}

		const ribbonLabelWidth = this.text.width; // dynamic text width

		this.ribbon = this.scene.make.nineslice({
			x: 0,
			y: 0,
			key: "main-settlement-name-ribbon",
			width: ribbonLabelWidth + 84 * 2, // left and right side
			height: 70,
			leftWidth: 84,
			rightWidth: 84,
			topHeight: 16,
			bottomHeight: 16,
			add: false, // leave false, as we'll add it to the container
		});

		this.ribbon.setOrigin(0.5, 0.5);

		this.labelContainer.add(this.ribbon);

		if (force) this.labelContainer.add(this.text);

		this.labelContainer.setDepth(TILE_LABEL_CORRECTION + 10);
	}

	public update(cameraZoom: number): void {
		const settingsStore = useSettingsStore();

		if (!this.labelContainer) return;
		if (!this.active) {
			this.labelContainer.setVisible(false);
			return;
		}
		if (
			[
				TileLabelSkin.QUEST_MAIN_E,
				TileLabelSkin.QUEST_MAIN_Q,
				TileLabelSkin.QUEST_SIDE_E,
				TileLabelSkin.QUEST_SIDE_Q,
			].includes(this.skin)
		) {
			this.scene.children.bringToTop(this.labelContainer);
		}
		const config = this.getConfig();
		const prevState = this.state;

		let fontSize = config.fontSize ?? 0;
		let fontColor = config.fontColor ?? 0x000000;

		if (this.skin === TileLabelSkin.TUTORIAL) {
			// resize podla vyrenderovaneho textu
			const widthPadding = 75;
			const tutsLabelBgWidth =
				this.text!.width + widthPadding ||
				(this.rawText?.length ?? 1) * 30;

			// const tutsLabelBgWidthMargin = 4;
			// this.resizeGroup1.forEach(obj => {
			// 	obj.x = (tutsLabelBgWidth / 2) * -1;
			// 	obj.width = tutsLabelBgWidth;
			// });
			// this.resizeGroup2.forEach(obj => {
			// 	obj.x = (tutsLabelBgWidth / 2) * -1 - tutsLabelBgWidthMargin;
			// 	obj.width = tutsLabelBgWidth + tutsLabelBgWidthMargin * 2;
			// });
			this.labelContainer.width = tutsLabelBgWidth;
			fontSize = 56;
			fontColor = 0x372615;
			// Ak sa veľkosť písma alebo farba zmenila od posledného updatu, prekreslíme to
			if (
				this.lastAppliedFontSize !== fontSize ||
				this.lastAppliedFontColor !== fontColor
			) {
				if (this.text) {
					this.text.setFontSize(fontSize);
					this.text.setTint(fontColor);
				}

				if (this.secondText) {
					this.secondText.setFontSize(fontSize);
					this.secondText.setTint(fontColor);
				}

				// Uložíme si nové hodnoty, aby sme to v ďalšom snímku už nemuseli robiť
				this.lastAppliedFontSize = fontSize;
				this.lastAppliedFontColor = fontColor;
			}

			this.labelContainer.setVisible(true);
		}

		if (this.skin === TileLabelSkin.MAIN_SETTLEMENT_NAME) {
			this.text!.setOrigin(0.5, 1.125);

			// Conditional display of main settlement name based on settings preference
			if (settingsStore.showMainSettlementNameLabel === false) {
				this.labelContainer.setVisible(false);
				return;
			}
		}

		if (
			[
				TileLabelType.SETTLEMENT,
				TileLabelType.MAIN_SETTLEMENT,
				TileLabelType.SETTLEMENT_NOT_CONNECTED,
			].includes(this.type) &&
			this.text
		) {
			// Cisto ak settings prebije
			if (
				settingsStore.showTownLevels === false &&
				[TileLabelSkin.LEVEL].includes(this.skin)
			) {
				fontSize = 46;
				this.text?.setOrigin(0.5, -1.25);
				this.bg?.setFrame(config.backgroundFrameAlt);
				this.state = 0;
				this.labelContainer.setX(
					this.coordinates.x * this.tilerepo.TILE_SIZE,
				);
				this.labelContainer.setY(
					this.coordinates.y * this.tilerepo.TILE_SIZE,
				);
				if (this.state !== prevState) {
					this.text.setFontSize(fontSize);
					this.text.setTint(fontColor);
				}

				const newText =
					(this.state === 0 ? "level " : "") +
					(useSettlementsStore()
						.getSettlementByCoordinates(this.coordinates)
						?.level.toString() ?? "");
				if (this.text?.text !== newText) {
					this.text.text = newText;
				}

				if (!this.scaleToZoom) return;

				if (cameraZoom > tileLabelZoomConfig[0]) {
					this.labelContainer.setVisible(false);
					return;
				} else {
					this.labelContainer.setVisible(true);
					this.labelContainer.setScale(1);
				}

				return;
			}

			if ([TileLabelSkin.LEVEL].includes(this.skin)) {
				// Settlement level label: text + number
				if (this.state !== 0 && cameraZoom >= tileLabelZoomConfig[1]) {
					fontSize = 46; // must be specified in px for consistent calculation given that we don't have a square but a rectangle
					this.bg?.setFrame(config.backgroundFrameAlt); // NOTE: here is a rectangle, but in the form of a square sprite
					this.state = 0;
					this.text?.setOrigin(
						0.5,
						-(this.tilerepo.TILE_SIZE - fontSize) /
							this.tilerepo.TILE_SIZE -
							0.4375,
					);
					this.labelContainer.setX(
						this.coordinates.x * this.tilerepo.TILE_SIZE,
					);
					this.labelContainer.setY(
						this.coordinates.y * this.tilerepo.TILE_SIZE,
					);
				}

				if (
					this.state !== 1 &&
					cameraZoom < tileLabelZoomConfig[1] &&
					cameraZoom >= tileLabelZoomConfig[2]
				) {
					this.text?.setOrigin(0.5, 1);
					this.bg?.setFrame(config.backgroundFrameMain);
					this.state = 1;
					this.labelContainer.setX(
						this.coordinates.x * this.tilerepo.TILE_SIZE +
							this.tilerepo.TILE_SIZE / 3.45,
					);
					this.labelContainer.setY(
						this.coordinates.y * this.tilerepo.TILE_SIZE +
							this.tilerepo.TILE_SIZE / 3.45,
					);
				}

				if (this.state !== 2 && cameraZoom < tileLabelZoomConfig[2]) {
					this.text?.setOrigin(0.5, 1);
					this.bg?.setFrame(config.backgroundFrameMain);
					this.state = 2;
					this.labelContainer.setX(
						this.coordinates.x * this.tilerepo.TILE_SIZE,
					);
					this.labelContainer.setY(
						this.coordinates.y * this.tilerepo.TILE_SIZE,
					);
				}
				if (this.state !== prevState) {
					this.text.setFontSize(fontSize);
					this.text.setTint(fontColor);
				}

				const newText =
					(this.state === 0 ? "level " : "") +
					(useSettlementsStore()
						.getSettlementByCoordinates(this.coordinates)
						?.level.toString() ?? "");
				if (this.text?.text !== newText) {
					this.text.text = newText;
				}
			}
		}

		if (
			!this.scaleToZoom ||
			this.skin.startsWith("expansionArrow") ||
			[TileLabelSkin.PLAGUE_GREEN, TileLabelSkin.PLAGUE_WHITE].includes(
				this.skin,
			)
		)
			return;

		const dpr = window.devicePixelRatio;
		const zoomBehavior = config.zoom ?? {};

		if (cameraZoom > tileLabelZoomConfig[0]) {
			if (config.tileSizeScaleMin) {
				this.labelContainer.setScale(1);
				return;
			} else if (zoomBehavior.scaleCoefficient === undefined) {
				this.labelContainer.setVisible(false);
				return;
			}
		}
		this.labelContainer.setVisible(true);

		if (zoomBehavior.respectsHideLabel) {
			this.labelContainer.setVisible(!this.hideLabel);
		}

		if (zoomBehavior.scaleCoefficient !== undefined) {
			if (
				zoomBehavior.hideBelowZoomIndex !== undefined &&
				cameraZoom <
					tileLabelZoomConfig[zoomBehavior.hideBelowZoomIndex]
			) {
				this.labelContainer.setVisible(false);
				return;
			}

			const minThreshold =
				zoomBehavior.hideBelowThresholdByDpr?.[dpr] ??
				tileLabelZoomBehaviorDefaults.hideBelowThresholdFallback;
			if (
				(zoomBehavior.hideBelowThresholdByDpr !== undefined &&
					cameraZoom <= minThreshold) ||
				cameraZoom > tileLabelZoomConfig[0]
			) {
				this.labelContainer.setVisible(false);
				return;
			}

			const TILE_SIZE = this.tilerepo.TILE_SIZE;
			const scale = Math.max(
				tileLabelZoomConfig[0],
				(1 / Math.max(cameraZoom, tileLabelZoomConfig[3])) *
					zoomBehavior.scaleCoefficient *
					dpr,
			);

			const offset =
				zoomBehavior.tutorialOffsetWithSettlementName !== undefined &&
				settingsStore.showMainSettlementNameLabel !== false
					? zoomBehavior.tutorialOffsetWithSettlementName
					: (zoomBehavior.offsetBase ??
						tileLabelZoomBehaviorDefaults.offsetBase);
			const offsetY =
				zoomBehavior.fixedOffsetAtMaxZoom &&
				cameraZoom >= tileLabelZoomConfig[0]
					? offset * dpr
					: (1 / Math.max(cameraZoom, tileLabelZoomConfig[3])) *
						offset *
						dpr;

			this.labelContainer.setScale(scale);
			this.labelContainer.setY(
				(this.coordinates.y + (this.coordinates.offsetY ?? 0)) *
					TILE_SIZE -
					TILE_SIZE / 2 -
					offsetY -
					(this.secondText ? 60 : 0),
			);
			return;
		}

		const SCALE_COEFFICIENT =
			(zoomBehavior.autoScaleCoefficient ??
				tileLabelZoomBehaviorDefaults.autoScaleCoefficient) * dpr;
		const tierMul = zoomBehavior.zoomTierMultipliers ?? {};

		if (cameraZoom >= tileLabelZoomConfig[1]) {
			this.labelContainer.setScale(1 * (tierMul[0] ?? 1));
		} else if (
			cameraZoom < tileLabelZoomConfig[1] &&
			cameraZoom >= tileLabelZoomConfig[2]
		) {
			if (config.tileSizeScaleMin) {
				this.labelContainer.setScale(1 * (tierMul[1] ?? 1));
				return;
			}
			this.labelContainer.setScale(
				tileLabelZoomBehaviorDefaults.midZoomBase * (tierMul[1] ?? 1),
			);
		} else if (
			cameraZoom < tileLabelZoomConfig[2] &&
			cameraZoom >= tileLabelZoomConfig[3]
		) {
			this.labelContainer.setScale(
				(1 / (cameraZoom ?? 1)) * SCALE_COEFFICIENT * (tierMul[2] ?? 1),
			);
		} else {
			this.labelContainer.setScale(
				(1 / tileLabelZoomConfig[3]) *
					SCALE_COEFFICIENT *
					(tierMul[3] ?? 1),
			);
		}
		if (this.sharingTile && cameraZoom >= tileLabelZoomConfig[2]) {
			// if this label is shared between multiple labels, we want to hide it on zoomed in levels
			this.labelContainer.setVisible(false);
		} else {
			this.labelContainer.setVisible(true);
		}
	}

	private getConfig(): TileLabelTypeConfig {
		let config = tileLabelTypes[TileLabelType.MISC];

		if (this.skin === TileLabelSkin.LEVEL) {
			config = tileLabelTypes[this.type];
		} else if (this.skin in tileLabelTypes) {
			config = tileLabelTypes[this.skin];
		}
		if (!config) {
			console.error(
				`Tile label type "${this.type}" or "${this.skin}" not defined!`,
			);
			return tileLabelTypes[TileLabelType.MISC];
		}
		return config;
	}

	public delete(): void {
		if (this.chestTimer) {
			this.chestTimer.destroy();
			this.chestTimer = null;
		}

		if (this.labelContainer) {
			this.labelContainer.destroy();
			this.labelContainer = null;
		}
	}

	public static formatKey(skin: TileLabelSkin, tileId: number): string {
		return `${skin}TileLabel${tileId}`;
	}

	public hideForWhile(): void {
		if (!this.labelContainer) return;
		this.hideLabel = true;

		this.scene.time.delayedCall(10_000, (): void => {
			this.hideLabel = false;
		});
	}

	public swapSkin(skin: TileLabelSkin): void {
		this.skin = skin;
		const config = this.getConfig();
		this.bg?.setFrame(config.backgroundFrameMain);
		if (this.skin === TileLabelSkin.CHEST) {
			if (!this.labelContainer) return;
			this.labelContainer.setInteractive();
			this.labelContainer.on(
				"pointerup",
				(pointer: Phaser.Input.Pointer): void => {
					if (pointer.downElement?.nodeName !== "CANVAS") return;

					this.shakeAnimation();
				},
			);
			this.chestTimer = this.scene.time.addEvent({
				delay: 10_000,
				callback: this.shakeAnimation,
				callbackScope: this,
				loop: true,
			});
		}
	}

	public updateLabelTexts(
		firstText: string | undefined,
		secondText: string | undefined,
	): void {
		if (this.text && firstText) {
			this.text.text = firstText;
		}
		if (!secondText) {
			this.secondText = null;
			return;
		}
		if (this.secondText) {
			this.secondText.text = secondText;
		}
	}
}

export default TileLabel;
